import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useClient } from "../../../apis/client/ClientProvider";
import { useBranch } from "../../../apis/branch/BranchProvider";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import Branch from "../../../types/branch";
import { FaChevronDown } from "react-icons/fa";
import AlertOverlay from "../../../components/Alert";

const ViewBranchById: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { id } = useParams<{ id: string }>();
    const { fetchBranchById, excluiBranch, putBranch } = useBranch();
    const { fetchClient, clients } = useClient();
    const [branch, setBranch] = useState<Branch | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const selectRef = useRef<(HTMLSelectElement | null)>(null);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

    const navItems: NavBarItem[] = [
        { label: t('branch.labels.home'), route: "/", iconName: "house-door" },
        { label: t('branch.labels.branchList'), route: "/Branch", iconName: "list" },
        { label: t('branch.labels.newBranch'), route: "/Branch/create", iconName: "plus-circle" },
    ];

    useEffect(() => {
        if (!id) throw new Error("ID inválido");

        const loadBranch = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error(t('branch.messages.invalidId'));

                const data = await fetchBranchById(Number(id));

                fetchClient(null);

                setBranch(data);
            } catch (err) {
                console.error(t('branch.messages.errorLoading'), err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadBranch();
    }, [id, fetchBranchById, fetchClient]);
    
    const handleClientChange = (e: React.ChangeEvent<any>) => {
        const { value } = e.target;

        setBranch((prevBranch) => {
            return {
                ...prevBranch,
                client: {
                    id: value
                }
            }
        });
    };

    const handleEdit = async () => {
        if (!branch) return;

        if (isEditing && branch.id) {
            try {
                const nullRequiredFields: string[] = [];
        
                if (!branch.name) nullRequiredFields.push("Name");
                if (!branch.client?.id) nullRequiredFields.push("Company");
                if (!branch.location?.latitude) nullRequiredFields.push("Lat");
                if (!branch.location?.longitude) nullRequiredFields.push("Lng");
                if (!branch.location?.address?.street) nullRequiredFields.push("Street");
                if (!branch.location?.address?.number) nullRequiredFields.push("Number");
                if (!branch.location?.address?.complement) nullRequiredFields.push("Complement");
                if (!branch.location?.address?.city) nullRequiredFields.push("City");
                if (!branch.location?.address?.state) nullRequiredFields.push("State");
                if (!branch.location?.address?.country) nullRequiredFields.push("Country");
                if (!branch.location?.address?.zipCode) nullRequiredFields.push("Zip Code");

                if (nullRequiredFields.length > 0) {
                    setInvalidFields(new Set(nullRequiredFields));
                    const warnMessage: string =
                        nullRequiredFields.length === 1
                            ? getUiInterface("fieldRequiredSingle", {
                                field: nullRequiredFields[0],
                            })
                            : getUiInterface("fieldRequiredMultiple", {
                                fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                                    ? ` ${getWordConnector("and")} `
                                    : ""
                                    }${nullRequiredFields.slice(-1)}`,
                            });

                    setAlert({ message: warnMessage, variant: "danger" });
                    return;
                } else {
                    setInvalidFields(new Set());
                }

                const sucesso = await putBranch(branch.id, branch);

                if (sucesso) {
                    const successMessage = getSuccessMessage("editBranch", { id: branch.id, });
                    navigate(`/Branch`, {
                        state: {
                            message: successMessage,
                            variant: "success",
                        },
                    });
                } else {
                    const errorMessage = getErrorMessage("editBranchError", { id: branch.id, });
                    setAlert({ message: errorMessage, variant: "danger" });
                }
            } catch (error) {
                setAlert({ message: t('branch.messages.errorUpdating'), variant: "danger" });
            }
        } else {
            setIsEditing(true);
        }
    };


    const handleDelete = async () => {
        if (!id) return;

        try {
            const sucesso = await excluiBranch(Number(id));

            if (sucesso) {
                const successMessage = getSuccessMessage("deleteBranch", { id: id });
                navigate(`/branch`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            }
            else {
                const errorMessage = getErrorMessage("deleteBranchError", { id: id, });
                setAlert({ message: errorMessage, variant: "danger" });
            }
        } catch (error) {
            setAlert({ message: t('branch.messages.errorDeleting'), variant: "danger" });
        }
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (branch) {
            const { name, value } = e.target;
    
            setBranch((prevBranch) => {
                const keys = name.split(".");
                const updatedBranch = { ...prevBranch };
                let current: any = updatedBranch;
    
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    current[key] = { ...current[key] };
                    current = current[key];
                }
    
                current[keys[keys.length - 1]] = value;
                
                return updatedBranch;
            });
        }
    };

    if (loading) {
        return <div className="text-center mt-4">{t('branch.messages.loading')}</div>;
    }

    if (error || !branch) {
        return <div className="text-center mt-4 text-danger">{t('branch.messages.errorLoading')}</div>;
    }

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editBranch") : getPageName("viewBranch")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group>
                            <Form.Label>{t('branch.fields.id')}</Form.Label>
                            <Form.Control size="sm" readOnly value={branch.id} style={{ width: "100%", backgroundColor: "#f0f0f0" }} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="name">
                            <Form.Label>{t('branch.fields.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={branch.name}
                                isInvalid={invalidFields.has("Name")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="cnpj">
                            <Form.Label>{t('branch.fields.cnpj')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="cnpj"
                                value={branch.cnpj}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="client">
                            <Form.Label>{t('branch.fields.client')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                {
                                    isEditing ? 
                                        <>
                                            <Form.Control
                                                as="select"
                                                name="client"
                                                value={branch.client?.id}
                                                isInvalid={invalidFields.has("Company")}
                                                onChange={handleClientChange}
                                                readOnly={!isEditing}
                                                ref={(el: HTMLSelectElement | null) => {
                                                    if (el) selectRef.current = el;
                                                }}
                                                style={{ 
                                                    backgroundImage: 'none', 
                                                    appearance: "none", 
                                                    paddingRight: "30px",
                                                    cursor: "pointer" 
                                                }}
                                            >
                                                <option value="">{t('branch.labels.selectCompany')}</option>
                                                {
                                                    (clients || []).map((client) => (
                                                        <option
                                                            key={client.id}
                                                            value={client.id}>
                                                            {`Id: ${client.id} | Nome: ${client.name}`}
                                                        </option>
                                                    ))
                                                }
                                            </Form.Control>
                                            <FaChevronDown
                                                style={{
                                                    position: "absolute",
                                                    right: "20px",
                                                    pointerEvents: "auto",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => selectRef?.current?.focus()}
                                            />
                                        </>
                                    :
                                        <>
                                            <Form.Control
                                                type="text"
                                                name="client2"
                                                value={(branch.client) ? `Id: ${branch.client?.id} | Nome: ${branch.client?.name}` : ""}
                                                readOnly={isEditing}
                                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                                            />
                                        </>
                                }
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <h5>{t('branch.fields.location')}</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>{t('branch.fields.lat')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={branch.location?.latitude}
                                isInvalid={invalidFields.has("Lat")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>{t('branch.fields.lng')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={branch.location?.longitude}
                                isInvalid={invalidFields.has("Lng")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('branch.fields.address')}</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>{t('branch.fields.street')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={branch.location?.address?.street}
                                isInvalid={invalidFields.has("Street")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>{t('branch.fields.number')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={branch.location?.address?.number}
                                isInvalid={invalidFields.has("Number")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>{t('branch.fields.complement')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={branch.location?.address?.complement}
                                isInvalid={invalidFields.has("Complement")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>{t('branch.fields.city')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={branch.location?.address?.city}
                                isInvalid={invalidFields.has("City")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>{t('branch.fields.state')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={branch.location?.address?.state}
                                isInvalid={invalidFields.has("State")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>{t('branch.fields.country')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={branch.location?.address?.country}
                                isInvalid={invalidFields.has("Country")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>{t('branch.fields.zipCode')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={branch.location?.address?.zipCode}
                                isInvalid={invalidFields.has("Zip Code")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewBranchById;
